import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  AlertCircle,
  BarChart3,
  Bell,
  Box,
  Boxes,
  Briefcase,
  ChevronDown,
  ChevronLeft,
  CreditCard,
  DollarSign,
  Files,
  Home,
  Layers,
  LogOut,
  Mail,
  Map,
  MapPin,
  Menu,
  MessageSquare,
  Moon,
  Package,
  Receipt,
  RefreshCcw,
  Search,
  Send,
  Settings,
  Shield,
  Sun,
  Tag,
  Ticket,
  TrendingUp,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { Buton, Iskelet, OnayDialog, cn, toast } from '@emanetx/ui';
import { useCikisYap, useOturumStore } from './oturum.js';
import { useTemaStore } from './tema.js';

type IkonTipi = React.ComponentType<{ size?: number; className?: string }>;

interface KenarOgesi {
  yol: string;
  etiket: string;
  ikon: IkonTipi;
}

interface KenarBolumu {
  baslik: string;
  ikon: IkonTipi;
  ogeler: KenarOgesi[];
}

const BOLUMLER: KenarBolumu[] = [
  {
    baslik: 'Genel',
    ikon: Home,
    ogeler: [{ yol: '/panel', etiket: 'Dashboard', ikon: Home }],
  },
  {
    baslik: 'İşlemler',
    ikon: Package,
    ogeler: [
      { yol: '/panel/kiralamalar', etiket: 'Kiralamalar', ikon: Package },
      { yol: '/panel/rezervasyonlar', etiket: 'Rezervasyonlar', ikon: Box },
      { yol: '/panel/odemeler', etiket: 'Ödemeler', ikon: CreditCard },
      { yol: '/panel/iadeler', etiket: 'İadeler', ikon: RefreshCcw },
    ],
  },
  {
    baslik: 'Yapı',
    ikon: Layers,
    ogeler: [
      { yol: '/panel/noktalar', etiket: 'Noktalar', ikon: MapPin },
      { yol: '/panel/olaylar', etiket: 'Cihaz Olayları', ikon: Activity },
    ],
  },
  {
    baslik: 'Müşteri',
    ikon: Users,
    ogeler: [
      { yol: '/panel/musteriler', etiket: 'Müşteriler', ikon: Users },
      { yol: '/panel/destek', etiket: 'Destek Talepleri', ikon: AlertCircle },
    ],
  },
  {
    baslik: 'İş Ortağı',
    ikon: Briefcase,
    ogeler: [
      { yol: '/panel/ortaklar', etiket: 'Ortaklar', ikon: Map },
      { yol: '/panel/hakedis', etiket: 'Hakediş', ikon: Wallet },
    ],
  },
  {
    baslik: 'Fiyatlandırma',
    ikon: DollarSign,
    ogeler: [
      { yol: '/panel/fiyat-kurallari', etiket: 'Kurallar', ikon: Tag },
      { yol: '/panel/kuponlar', etiket: 'Kuponlar', ikon: Ticket },
      { yol: '/panel/doviz', etiket: 'Döviz Kurları', ikon: DollarSign },
    ],
  },
  {
    baslik: 'Raporlar',
    ikon: BarChart3,
    ogeler: [
      { yol: '/panel/raporlar/gelir', etiket: 'Gelir', ikon: BarChart3 },
      { yol: '/panel/raporlar/analitik', etiket: 'Analitik', ikon: Activity },
    ],
  },
  {
    baslik: 'Muhasebe',
    ikon: Receipt,
    ogeler: [
      { yol: '/panel/muhasebe/masraflar', etiket: 'Masraflar', ikon: Receipt },
      { yol: '/panel/muhasebe/karlilik', etiket: 'Karlılık', ikon: TrendingUp },
    ],
  },
  {
    baslik: 'Sistem',
    ikon: Settings,
    ogeler: [
      { yol: '/panel/kullanicilar', etiket: 'Kullanıcılar', ikon: Shield },
      { yol: '/panel/ayarlar', etiket: 'Ayarlar', ikon: Settings },
      { yol: '/panel/ayarlar/smtp', etiket: 'SMTP', ikon: Send },
      { yol: '/panel/ayarlar/sablonlar', etiket: 'E-posta Şablonları', ikon: Mail },
      { yol: '/panel/ayarlar/netgsm', etiket: 'NetGSM SMS', ikon: MessageSquare },
      { yol: '/panel/ayarlar/akbank', etiket: 'Akbank Sanal POS', ikon: CreditCard },
      { yol: '/panel/bildirim/promosyon', etiket: 'Promosyon Push', ikon: Bell },
      { yol: '/panel/denetim', etiket: 'Denetim Logları', ikon: Files },
      { yol: '/panel/aktivite', etiket: 'Müşteri Aktivitesi', ikon: Activity },
    ],
  },
];

// Genel-bolumun tek bir elemani var, dogrudan link gibi davransin
const TEK_ELEMAN_BOLUM_BASLIK = 'Genel';

export function AnaYerlesim({ children }: { children: React.ReactNode }) {
  const [masaustuDaralt, setMasaustuDaralt] = useState(false);
  const [mobilAcik, setMobilAcik] = useState(false);
  const konum = useLocation();

  // Sayfa degisince mobil drawer'i kapat
  useEffect(() => {
    setMobilAcik(false);
  }, [konum.pathname]);

  // Drawer acikken body scroll lock
  useEffect(() => {
    if (mobilAcik) document.body.classList.add('no-kaydir');
    else document.body.classList.remove('no-kaydir');
    return () => document.body.classList.remove('no-kaydir');
  }, [mobilAcik]);

  return (
    <div className="bg-fume-50 text-fume-900 dark:bg-fume-950 dark:text-fume-50 min-h-screen">
      {/* Mobil backdrop */}
      {mobilAcik && (
        <button
          onClick={() => setMobilAcik(false)}
          aria-label="Menüyü kapat"
          className="bg-fume-900/60 fixed inset-0 z-30 backdrop-blur-sm lg:hidden"
        />
      )}

      <div className="flex">
        <KenarCubugu
          masaustuDaralt={masaustuDaralt}
          setMasaustuDaralt={setMasaustuDaralt}
          mobilAcik={mobilAcik}
          setMobilAcik={setMobilAcik}
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <UstCubuk onMobilAc={() => setMobilAcik(true)} />
          <main className="mx-auto w-full max-w-[1440px] p-4 sm:p-6 md:p-8">{children}</main>
        </div>
      </div>
    </div>
  );
}

function KenarCubugu({
  masaustuDaralt,
  setMasaustuDaralt,
  mobilAcik,
  setMobilAcik,
}: {
  masaustuDaralt: boolean;
  setMasaustuDaralt: (v: boolean) => void;
  mobilAcik: boolean;
  setMobilAcik: (v: boolean) => void;
}) {
  const konum = useLocation();
  const kullanici = useOturumStore((s) => s.kullanici);
  const cikis = useCikisYap();
  const navigate = useNavigate();
  const [cikisOnay, setCikisOnay] = useState(false);

  return (
    <>
      <aside
        className={cn(
          // Mobile: fixed drawer; desktop: sticky kalici
          'border-fume-200 dark:border-fume-800 dark:bg-fume-900 fixed inset-y-0 left-0 z-40 flex h-screen flex-col border-r bg-white shadow-2xl transition-[transform,width] duration-200',
          'lg:sticky lg:top-0 lg:z-auto lg:shadow-none',
          // Mobile drawer width sabit
          'w-[280px]',
          // Desktop genislik
          masaustuDaralt ? 'lg:w-[72px]' : 'lg:w-[260px]',
          // Mobile slide
          mobilAcik ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        {/* Tepe — logo + collapse/close */}
        <div className="border-fume-100 dark:border-fume-800 flex h-16 items-center justify-between border-b px-4">
          <Link to="/panel" className="flex items-center gap-2 text-lg font-bold tracking-tight">
            <span className="bg-marka-500 text-fume-900 inline-grid h-8 w-8 place-items-center rounded-lg font-black">
              E
            </span>
            <span className={cn('lg:inline', masaustuDaralt && 'lg:hidden')}>
              Emanet<span className="text-marka-500">X</span>
            </span>
          </Link>

          {/* Desktop collapse */}
          <button
            onClick={() => setMasaustuDaralt(!masaustuDaralt)}
            className="text-fume-400 hover:text-fume-900 dark:hover:text-fume-100 hidden lg:block"
            aria-label="Kenar çubuğunu daralt/genişlet"
          >
            <ChevronLeft
              className={cn('transition-transform', masaustuDaralt && 'rotate-180')}
              size={18}
            />
          </button>

          {/* Mobile close */}
          <button
            onClick={() => setMobilAcik(false)}
            className="text-fume-500 hover:text-fume-900 dark:hover:text-fume-100 lg:hidden"
            aria-label="Menüyü kapat"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigasyon — accordion gruplar */}
        <nav className="flex-1 overflow-y-auto py-3">
          {BOLUMLER.map((b) => (
            <BolumAccordion
              key={b.baslik}
              bolum={b}
              konumYolu={konum.pathname}
              masaustuDaralt={masaustuDaralt}
            />
          ))}
        </nav>

        {/* Alt — kullanici karti */}
        <div className="border-fume-100 dark:border-fume-800 border-t p-3">
          <div className="flex items-center gap-3">
            <div className="bg-marka-500 text-fume-900 grid h-9 w-9 flex-shrink-0 place-items-center rounded-full text-sm font-bold">
              {(kullanici?.adSoyad ?? 'E').slice(0, 1).toUpperCase()}
            </div>
            <div className={cn('min-w-0 flex-1', masaustuDaralt && 'lg:hidden')}>
              <p className="truncate text-sm font-medium">{kullanici?.adSoyad ?? '—'}</p>
              <p className="text-fume-500 truncate text-xs">{kullanici?.eposta}</p>
            </div>
            <button
              onClick={() => setCikisOnay(true)}
              className={cn('text-fume-400 hover:text-red-600', masaustuDaralt && 'lg:hidden')}
              aria-label="Çıkış"
              title="Çıkış"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      <OnayDialog
        acik={cikisOnay}
        onDegisim={setCikisOnay}
        baslik="Çıkış yap"
        aciklama="Oturumunuz sonlandırılacak. Tekrar girmek için bilgilerinizi yeniden girmeniz gerekecek."
        onayMetni="Evet, çıkış yap"
        iptalMetni="Vazgeç"
        tur="uyari"
        onOnay={async () => {
          await cikis.mutateAsync();
          toast.success('Çıkış yapıldı');
          void navigate({ to: '/giris' });
        }}
      />
    </>
  );
}

function UstCubuk({ onMobilAc }: { onMobilAc: () => void }) {
  const { t, i18n } = useTranslation('admin');
  const tema = useTemaStore((s) => s.tema);
  const degistir = useTemaStore((s) => s.degistir);
  const kullanici = useOturumStore((s) => s.kullanici);

  return (
    <header className="border-fume-200 dark:border-fume-800 dark:bg-fume-900/80 sticky top-0 z-20 flex h-16 items-center gap-2 border-b bg-white/90 px-3 backdrop-blur sm:gap-3 sm:px-6">
      {/* Hamburger — mobil */}
      <button
        onClick={onMobilAc}
        className="text-fume-700 hover:bg-fume-100 dark:text-fume-200 dark:hover:bg-fume-800 grid h-9 w-9 place-items-center rounded-lg lg:hidden"
        aria-label="Menüyü aç"
      >
        <Menu size={20} />
      </button>

      {/* Arama */}
      <div className="hidden max-w-xl flex-1 sm:relative sm:block">
        <Search size={16} className="text-fume-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="search"
          placeholder={t('yerlesim.arama')}
          className="bg-fume-100 placeholder:text-fume-400 focus:ring-marka-500 dark:bg-fume-800 dark:placeholder:text-fume-500 h-9 w-full rounded-lg pl-9 pr-3 text-sm focus:outline-none focus:ring-2"
        />
      </div>
      <div className="flex-1 sm:hidden" />

      {/* Mobile arama ikon */}
      <button
        className="text-fume-700 hover:bg-fume-100 dark:text-fume-200 dark:hover:bg-fume-800 grid h-9 w-9 place-items-center rounded-lg sm:hidden"
        aria-label="Ara"
        onClick={() => toast.info('Cmd+K araması sonraki adımda eklenecek.')}
      >
        <Search size={18} />
      </button>

      {/* Bildirimler */}
      <button
        className="text-fume-700 hover:bg-fume-100 dark:text-fume-200 dark:hover:bg-fume-800 relative grid h-9 w-9 place-items-center rounded-lg"
        aria-label={t('yerlesim.bildirimler')}
      >
        <Bell size={18} />
        <span className="bg-marka-500 dark:ring-fume-900 absolute right-2 top-2 h-2 w-2 rounded-full ring-2 ring-white" />
      </button>

      {/* Dark mode */}
      <button
        onClick={degistir}
        className="text-fume-700 hover:bg-fume-100 dark:text-fume-200 dark:hover:bg-fume-800 grid h-9 w-9 place-items-center rounded-lg"
        aria-label={t('yerlesim.karanlikMod')}
      >
        {tema === 'karanlik' ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      {/* Dil */}
      <button
        onClick={() => {
          const sonraki = i18n.language === 'tr' ? 'en' : 'tr';
          void i18n.changeLanguage(sonraki);
        }}
        className="text-fume-700 hover:bg-fume-100 dark:text-fume-200 dark:hover:bg-fume-800 hidden h-9 min-w-9 items-center justify-center rounded-lg px-2 text-xs font-bold uppercase sm:grid"
        aria-label={t('yerlesim.dil')}
      >
        {i18n.language.toUpperCase()}
      </button>

      {/* Avatar */}
      <div className="border-fume-200 dark:border-fume-800 flex items-center gap-2 border-l pl-2 sm:pl-3">
        {kullanici ? (
          <>
            <div className="bg-marka-500 text-fume-900 grid h-8 w-8 place-items-center rounded-full text-sm font-bold">
              {kullanici.adSoyad.slice(0, 1).toUpperCase()}
            </div>
            <div className="hidden md:block">
              <p className="text-sm font-medium leading-tight">{kullanici.adSoyad}</p>
              <p className="text-fume-500 text-xs">{kullanici.roller?.[0] ?? '—'}</p>
            </div>
          </>
        ) : (
          <Iskelet className="h-8 w-8 rounded-full" />
        )}
      </div>
    </header>
  );
}

/**
 * Sidebar grup accordion'u — baslik tikla aç/kapa.
 * Aktif route grupta varsa otomatik açilir kalir.
 * Tek elemanli grup (Genel/Dashboard) direkt link gibi davranir.
 */
function BolumAccordion({
  bolum,
  konumYolu,
  masaustuDaralt,
}: {
  bolum: KenarBolumu;
  konumYolu: string;
  masaustuDaralt: boolean;
}) {
  const aktifGrupta = bolum.ogeler.some((o) => konumYolu === o.yol);
  const [acik, setAcik] = useState(aktifGrupta);

  // Aktif route grup içine geçince otomatik aç
  useEffect(() => {
    if (aktifGrupta) setAcik(true);
  }, [aktifGrupta]);

  const tekEleman = bolum.ogeler.length === 1 && bolum.baslik === TEK_ELEMAN_BOLUM_BASLIK;

  if (tekEleman) {
    const o = bolum.ogeler[0]!;
    const Ikon = o.ikon;
    const aktif = konumYolu === o.yol;
    return (
      <div className="mb-1">
        <Link
          to={o.yol}
          className={cn(
            'flex items-center gap-3 border-l-2 px-4 py-2.5 text-sm font-medium transition-colors',
            aktif
              ? 'border-marka-500 bg-marka-50 text-fume-900 dark:bg-marka-900/20 dark:text-marka-300'
              : 'text-fume-700 hover:bg-fume-50 dark:text-fume-300 dark:hover:bg-fume-800/50 border-transparent',
          )}
          title={masaustuDaralt ? o.etiket : undefined}
        >
          <Ikon
            size={18}
            className={cn('flex-shrink-0', aktif ? 'text-marka-600 dark:text-marka-400' : '')}
          />
          <span className={cn('truncate', masaustuDaralt && 'lg:hidden')}>{o.etiket}</span>
        </Link>
      </div>
    );
  }

  const GrupIkon = bolum.ikon;

  // Daraltilmis modda her grup linkleri tooltip ile sirali gozuksun
  if (masaustuDaralt) {
    return (
      <div className="mb-1 hidden lg:block">
        {bolum.ogeler.map((o) => {
          const Ikon = o.ikon;
          const aktif = konumYolu === o.yol;
          return (
            <Link
              key={o.yol}
              to={o.yol}
              title={o.etiket}
              className={cn(
                'flex items-center justify-center border-l-2 py-2.5 transition-colors',
                aktif
                  ? 'border-marka-500 bg-marka-50 text-marka-700 dark:bg-marka-900/20 dark:text-marka-300'
                  : 'text-fume-500 hover:bg-fume-50 dark:hover:bg-fume-800/50 border-transparent',
              )}
            >
              <Ikon size={18} />
            </Link>
          );
        })}
      </div>
    );
  }

  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() => setAcik(!acik)}
        className={cn(
          'flex w-full items-center gap-3 border-l-2 border-transparent px-4 py-2 text-left text-[11px] font-bold uppercase tracking-wider transition-colors',
          aktifGrupta
            ? 'text-marka-600 dark:text-marka-400'
            : 'text-fume-500 dark:text-fume-400 hover:text-fume-700 dark:hover:text-fume-200',
        )}
      >
        <GrupIkon size={14} className="flex-shrink-0" />
        <span className="flex-1 truncate">{bolum.baslik}</span>
        <ChevronDown size={14} className={cn('transition-transform', acik && 'rotate-180')} />
      </button>
      {acik && (
        <ul className="mt-0.5">
          {bolum.ogeler.map((o) => {
            const Ikon = o.ikon;
            const aktif = konumYolu === o.yol;
            return (
              <li key={o.yol}>
                <Link
                  to={o.yol}
                  className={cn(
                    'flex items-center gap-3 border-l-2 py-2 pl-9 pr-4 text-sm transition-colors',
                    aktif
                      ? 'border-marka-500 bg-marka-50 text-fume-900 dark:bg-marka-900/20 dark:text-marka-300 font-medium'
                      : 'text-fume-600 hover:bg-fume-50 dark:text-fume-300 dark:hover:bg-fume-800/50 border-transparent',
                  )}
                >
                  <Ikon
                    size={15}
                    className={cn(
                      'flex-shrink-0',
                      aktif ? 'text-marka-600 dark:text-marka-400' : 'text-fume-400',
                    )}
                  />
                  <span className="truncate">{o.etiket}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function YuklemeYerlesimi() {
  return (
    <div className="bg-fume-50 dark:bg-fume-950 grid min-h-screen place-items-center">
      <div className="text-fume-500 flex flex-col items-center gap-3">
        <div className="bg-marka-500 text-fume-900 grid h-12 w-12 animate-pulse place-items-center rounded-xl font-black">
          E
        </div>
        <Buton stil="cizgili" disabled>
          Oturum kontrol ediliyor...
        </Buton>
      </div>
    </div>
  );
}
